import { type FC, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import { CALENDAR_INTERVAL_OPTIONS, FREQUENCY_MAP } from '../../config/calendarConstants';
import { TPO_CALENDAR_INTERVAL_MODAL, TPO_CALENDAR_PAGE } from '../../config/testIds';
import type { CalendarIntervalType, CalendarRow } from '../../types/calendar';
import { InfeasibilityStatus } from '../../types/infeasiblePlan';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditSession } from '../EditSession/EditSessionContext';
import { sendNotification } from 'utils/helperMethods';
import useCalendarData from './hooks/queries/useCalendarData';
import useOptimizationId from './hooks/queries/useOptimizationId';
import useOptimizationPolling from './hooks/queries/useOptimizationPolling';
import useInfeasibilityPolling from './hooks/queries/useInfeasibilityPolling';
import { useCalendarSort } from './hooks/ui/useCalendarSort';
import { useContainerWidth } from './hooks/ui/useContainerWidth';
import { useCalendarColumns } from './hooks/ui/useCalendarColumns';
import { usePromoTypeLegend } from './hooks/ui/usePromoTypeLegend';
import { useCalendarRowActions, type KeepFlag } from './hooks/ui/useCalendarRowActions';
import CalendarToolbar from './CalendarToolbar';
import CalendarTable from './CalendarTable';
import PlanDetailsFooter from '../components/PlanDetailsFooter';
import ViewSelectModal from '../components/ViewSelectModal';
import DeletePlanModal from './components/DeletePlanModal';
import SaveAsPlanModal from './components/SaveAsPlanModal';
import SaveAsOriginalModal from './components/SaveAsOriginalModal';
import DeleteAllPromotionsModal from './components/DeleteAllPromotionsModal';
import DeleteEventModal from './components/DeleteEventModal';
import KeepFlagsModal, { type PendingKeepFlag } from './components/KeepFlagsModal';
import KeepEventModal from './components/KeepEventModal';
import ImportEventFromPlanModal from './components/ImportEventFromPlanModal';
import InfeasiblePlanDrawer from './components/InfeasiblePlanDrawer';
import { OptimizePlanModal } from './components/OptimizePlan';
import type { OptimizationSubmitContext } from './components/OptimizePlan';
import { OptimiseResultModal } from './components/OptimiseResult';
import ReportExportModal from '../../ReportExport/ReportExportModal';
import { useExportJobs } from '../../ReportExport/context/ExportJobsContext';
import { buildCalendarExportPayload } from '../../ReportExport/utils/exportPayloadBuilders';
import type { ExportScope } from '../../ReportExport/types';
import { LABELS as tpoGenLabels } from '../../config/labels';
import type { RunOptimizationResponse } from './hooks/mutations/useRunOptimizationMutation';
import { TPOGEN_OPTIMIZE_COMPLETED, TPOGEN_OPTIMIZE_FAILED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';

interface CalendarViewProps {
    planId: string;
    planName: string;
}

export const CalendarView: FC<CalendarViewProps> = ({ planId, planName }) => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const {
        currentPlanDetails,
        calendarFilterState,
        optimizedPlanId,
        setOptimizedPlanId,
        showOptimizedView: showOptimizedCalendar,
        setShowOptimizedView: setShowOptimizedCalendar,
        setOptimizationPollingStatus,
        calendarSelectedColumnKeys: selectedColumnKeys,
        setCalendarSelectedColumnKeys: setSelectedColumnKeys,
        calendarGroupBy: groupBy,
        setCalendarGroupBy: setGroupBy,
        calendarExpandedRowKeys: expandedRowKeys,
        setCalendarExpandedRowKeys: setExpandedRowKeys,
    } = usePlanDetailsContext();
    const tpoGenConfig = useTpoGenConfig();
    const { isEstablishingEditAccess } = useEditSession();
    const { isExportPending } = useExportJobs();

    const [calendarInterval, setCalendarInterval] = useState<CalendarIntervalType>(
        tpoGenConfig.defaultCalendarInterval || CALENDAR_INTERVAL_OPTIONS[0].value,
    );
    const [showIntervalModal, setShowIntervalModal] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [showChanges, setShowChanges] = useState(false);
    const [showDeletePlan, setShowDeletePlan] = useState(false);
    const [showSaveAs, setShowSaveAs] = useState(false);
    const [showSaveAsOriginal, setShowSaveAsOriginal] = useState(false);
    const [showDeleteAllPromotions, setShowDeleteAllPromotions] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [pendingDeleteEvent, setPendingDeleteEvent] = useState<{ eventId: string; eventName: string } | null>(null);
    const [pendingKeepFlag, setPendingKeepFlag] = useState<PendingKeepFlag | null>(null);
    const [pendingKeepEvent, setPendingKeepEvent] = useState<CalendarRow | null>(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showOptimizePlanModal, setShowOptimizePlanModal] = useState(false);
    const [showOptimiseResultModal, setShowOptimiseResultModal] = useState(false);
    const [optimizationJobId, setOptimizationJobId] = useState<string | null>(null);
    // Held separately from `optimizationJobId`, which a terminal outcome clears:
    // the infeasibility diagnosis is fetched against the job that produced it.
    const [infeasibleJobId, setInfeasibleJobId] = useState<string | null>(null);
    const [showInfeasibleDrawer, setShowInfeasibleDrawer] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const isUserTriggeredOptimization = useRef(false);
    const erroredJobIdRef = useRef<string | null>(null);
    // Populated when the user submits the Optimize modal; used to attach `objective`/
    // `constraint_count`/`duration_ms` to the completed/failed analytics events, which
    // fire later from the polling callbacks below (no other data path connects them).
    const optimizationTrackingRef = useRef<{ objective: string | null; constraintCount: number; startedAt: number } | null>(
        null,
    );

    const { optimizationId: existingOptimizationId } = useOptimizationId(planId);

    useEffect(() => {
        if (existingOptimizationId && !optimizationJobId && erroredJobIdRef.current !== existingOptimizationId) {
            setOptimizationJobId(existingOptimizationId);
        }
    }, [existingOptimizationId, optimizationJobId]);

    const handleOptimizationCompleted = useCallback((completedPlanId: string | null) => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('success', 'Optimization completed successfully.');
            const tracking = optimizationTrackingRef.current;
            trackTpoGenEvent(TPOGEN_OPTIMIZE_COMPLETED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                plan_id: planId,
                objective: tracking?.objective ?? undefined,
                constraint_count: tracking?.constraintCount,
                duration_ms: tracking ? Math.round(performance.now() - tracking.startedAt) : undefined,
            });
        }
        if (completedPlanId) {
            setOptimizedPlanId(completedPlanId);
            setShowOptimizedCalendar(true);
        }
    }, [planId, setOptimizedPlanId, setShowOptimizedCalendar]);

    const handleOptimizationError = useCallback(() => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('error', tpoGenLabels.optimizePlan.notifications.optimizationFailed);
            const tracking = optimizationTrackingRef.current;
            trackTpoGenEvent(TPOGEN_OPTIMIZE_FAILED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
                plan_id: planId,
                objective: tracking?.objective ?? undefined,
                failure_reason: tpoGenLabels.optimizePlan.notifications.optimizationFailed,
                duration_ms: tracking ? Math.round(performance.now() - tracking.startedAt) : undefined,
            });
        }
        setOptimizationJobId((prev) => {
            erroredJobIdRef.current = prev;
            return null;
        });
    }, [planId]);

    const handleOptimizationInfeasible = useCallback((jobId: string) => {
        if (isUserTriggeredOptimization.current) {
            sendNotification('info', tpoGenLabels.optimizePlan.notifications.infeasibleToast);
        }
        setInfeasibleJobId(jobId);
        setOptimizationJobId((prev) => {
            erroredJobIdRef.current = prev;
            return null;
        });
    }, []);

    const {
        optimizationStatus,
        optimizationOutcome,
        optimizationTitle,
        optimizationMessage,
        optimizedPlanId: polledOptimizedPlanId,
    } = useOptimizationPolling({
        jobId: optimizationJobId,
        onCompleted: handleOptimizationCompleted,
        onError: handleOptimizationError,
        onInfeasible: handleOptimizationInfeasible,
    });

    const { infeasiblePlanConfig } = tpoGenConfig;
    const { infeasibilityStatus, infeasibilityData } = useInfeasibilityPolling({
        jobId: infeasibleJobId,
        enabled: Boolean(infeasibleJobId),
        pollingIntervalMs: infeasiblePlanConfig.pollingIntervalMs,
    });

    // A terminal outcome (infeasible / error) clears the job id, which disables the
    // polling query and drops its data. Keep the last known status so the header can
    // still report the outcome; it is reset when a new optimization is submitted.
    useEffect(() => {
        if (!optimizationStatus) return;
        setOptimizationPollingStatus({
            status: optimizationStatus,
            outcome: optimizationOutcome,
            title: optimizationTitle,
            message: optimizationMessage,
        });
    }, [
        optimizationStatus,
        optimizationOutcome,
        optimizationTitle,
        optimizationMessage,
        setOptimizationPollingStatus,
    ]);

    useEffect(() => {
        if (optimizationStatus === 'completed' && polledOptimizedPlanId && !optimizedPlanId) {
            setOptimizedPlanId(polledOptimizedPlanId);
            setShowOptimizedCalendar(true);
        }
    }, [optimizationStatus, polledOptimizedPlanId, optimizedPlanId, setOptimizedPlanId, setShowOptimizedCalendar]);

    const { sortBy, sortOrder, handleTableChange, getColumnSortOrder } = useCalendarSort();
    const containerWidth = useContainerWidth(containerRef);

    const frequency = FREQUENCY_MAP[calendarInterval];
    const activePlanId = showOptimizedCalendar && optimizedPlanId ? optimizedPlanId : planId;
    const calendarData = useCalendarData(
        activePlanId,
        frequency,
        searchKeyword,
        groupBy,
        selectedColumnKeys,
        calendarFilterState.filters,
        sortBy,
        sortOrder,
        tpoGenConfig.calendarBarFieldMap,
        tpoGenConfig.fieldValueLabelMaps,
        tpoGenConfig.allDefaultColumns,
        tpoGenConfig.calendarGroupByOptions,
    );

    // The diagnosis lands minutes after the run finished, so the toast is what
    // tells the user the factors are now available behind the toolbar button.
    // Only a run they started in this session is worth interrupting them for.
    const prevInfeasibilityStatusRef = useRef(infeasibilityStatus);
    useEffect(() => {
        if (prevInfeasibilityStatusRef.current === infeasibilityStatus) return;
        prevInfeasibilityStatusRef.current = infeasibilityStatus;

        if (infeasibilityStatus === InfeasibilityStatus.COMPLETED && isUserTriggeredOptimization.current) {
            sendNotification('success', infeasiblePlanConfig.labels.pollingCompleteNotification);
        }
    }, [infeasibilityStatus, infeasiblePlanConfig.labels.pollingCompleteNotification]);

    const handleOptimizationSubmitFinish = useCallback(
        (data: RunOptimizationResponse, context: OptimizationSubmitContext) => {
            isUserTriggeredOptimization.current = true;
            optimizationTrackingRef.current = {
                objective: context.objective,
                constraintCount: context.constraintCount,
                startedAt: performance.now(),
            };
            setOptimizationPollingStatus(null);
            setInfeasibleJobId(null);
            setShowInfeasibleDrawer(false);
            setOptimizationJobId(data.job_id);
            setShowOptimizePlanModal(false);
        },
        [setOptimizationPollingStatus],
    );

    const handleRequestDeleteEvent = useCallback((record: CalendarRow) => {
        setPendingDeleteEvent({ eventId: record.event_id, eventName: record.event_name });
    }, []);

    const handleRequestKeepFlag = useCallback((record: CalendarRow, flag: KeepFlag) => {
        setPendingKeepFlag({ record, flag });
    }, []);

    const handleRequestKeepEvent = useCallback((record: CalendarRow) => {
        setPendingKeepEvent(record);
    }, []);

    const { getRowActionMenuItems, handleEditEvent, handleEditPromo } = useCalendarRowActions({
        onRequestDeleteEvent: handleRequestDeleteEvent,
        onRequestKeepFlag: handleRequestKeepFlag,
        onRequestKeepEvent: handleRequestKeepEvent,
    });
    const legend = usePromoTypeLegend(calendarData.dataSource, calendarData.periods);
    const handleToggleShowChanges = useCallback(() => {
        setShowChanges((prev) => !prev);
    }, []);

    const handleShowExportModal = useCallback(() => setShowExportModal(true), []);
    const handleCloseExportModal = useCallback(() => setShowExportModal(false), []);

    // `group_by` is deliberately absent: a grouped calendar exports flat, so the
    // backend never has to emit parent/child rows.
    const buildCalendarPayload = useCallback(
        (scope: ExportScope) =>
            buildCalendarExportPayload({
                scope,
                frequency,
                sortBy,
                sortingOrder: sortOrder,
                searchString: searchKeyword,
                searchFields: selectedColumnKeys,
                filters: calendarFilterState.filters,
                totalCount: calendarData.totalCount,
            }),
        [
            frequency,
            sortBy,
            sortOrder,
            searchKeyword,
            selectedColumnKeys,
            calendarFilterState.filters,
            calendarData.totalCount,
        ],
    );

    const handleClickInterval = useCallback(() => setShowIntervalModal(true), []);
    const handleApplyInterval = useCallback((value: CalendarIntervalType) => {
        setCalendarInterval(value);
        setShowIntervalModal(false);
    }, []);

    const columns = useCalendarColumns({
        periods: calendarData.periods,
        frequency,
        containerWidth,
        expandedRowKeys,
        setExpandedRowKeys,
        onClickInterval: handleClickInterval,
        selectedColumnKeys,
        sortBy,
        sortOrder,
        getColumnSortOrder,
        getRowActionMenuItems,
        showChanges,
        onEditEvent: handleEditEvent,
        onEditPromo: handleEditPromo,
    });

    // A create-flow auto-acquire still resolving counts as "loading" here too, so the
    // table shows its normal loading spinner instead of briefly rendering read-only.
    const isCalendarDataLoading =
        isEstablishingEditAccess ||
        calendarData.isPending ||
        (calendarData.isFetching && !calendarData.isFetchingNextPage);

    return (
        <div data-testid={TPO_CALENDAR_PAGE}>
            <CalendarToolbar
                planId={planId}
                selectedColumnKeys={selectedColumnKeys}
                setSelectedColumnKeys={setSelectedColumnKeys}
                searchKeyword={searchKeyword}
                setSearchKeyword={setSearchKeyword}
                groupBy={groupBy}
                setGroupBy={setGroupBy}
                filterState={calendarFilterState}
                showChanges={showChanges}
                onToggleShowChanges={handleToggleShowChanges}
                onShowDeletePlan={() => setShowDeletePlan(true)}
                onShowDeleteAllPromotions={() => setShowDeleteAllPromotions(true)}
                onShowImportModal={() => setShowImport(true)}
                onShowOptimizePlan={() => setShowOptimizePlanModal(true)}
                onShowOptimisationResults={() => setShowOptimiseResultModal(true)}
                onShowSaveAs={() => setShowSaveAs(true)}
                onShowSaveAsOriginal={() => setShowSaveAsOriginal(true)}
                onShowExportModal={handleShowExportModal}
                isExportPending={isExportPending(activePlanId, 'calendar')}
                isOptimizationInProgress={optimizationStatus === 'in_progress'}
                optimizationCompleted={!!optimizedPlanId}
                isDataLoading={isCalendarDataLoading}
                isInfeasible={!!infeasibleJobId}
                infeasibilityStatus={infeasibilityStatus}
                onShowInfeasibleDrawer={() => setShowInfeasibleDrawer(true)}
            />

            <CalendarTable
                ref={containerRef}
                dataSource={calendarData.dataSource}
                columns={columns}
                isPending={calendarData.isPending || isEstablishingEditAccess}
                isFetching={calendarData.isFetching}
                isFetchingNextPage={calendarData.isFetchingNextPage}
                hasNextPage={calendarData.hasNextPage}
                fetchNextPage={calendarData.fetchNextPage}
                expandedRowKeys={expandedRowKeys}
                setExpandedRowKeys={setExpandedRowKeys}
                handleTableChange={handleTableChange}
                legend={legend}
                showChanges={showChanges}
            />

            <PlanDetailsFooter
                loadedCount={calendarData.loadedCount}
                totalCount={calendarData.totalCount}
                createdBy={currentPlanDetails?.created_by}
            />

            <DeletePlanModal
                open={showDeletePlan}
                onClose={() => setShowDeletePlan(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName}
                onSuccess={() =>
                    navigate(`/tpo-gen/plans-index${state?.fromSearch ?? ''}`, { replace: true })
                }
            />
            <SaveAsPlanModal
                open={showSaveAs}
                onClose={() => setShowSaveAs(false)}
                planId={activePlanId}
                planName={currentPlanDetails?.plan_name || planName}
                onSuccess={(data, savedName) =>
                    navigate(`/tpo-gen/plan/${data.new_plan_id}`, {
                        state: { planId: data.new_plan_id, planName: savedName },
                        replace: true,
                    })
                }
            />
            <SaveAsOriginalModal
                open={showSaveAsOriginal}
                onClose={() => setShowSaveAsOriginal(false)}
                optimizedPlanId={optimizedPlanId ?? ''}
                onSuccess={(data) => {
                    setOptimizedPlanId(null);
                    setShowOptimizedCalendar(false);
                    navigate(`/tpo-gen/plan/${data.plan_id}`, {
                        state: { planId: data.plan_id },
                        replace: true,
                    });
                }}
            />
            <DeleteAllPromotionsModal
                open={showDeleteAllPromotions}
                onClose={() => setShowDeleteAllPromotions(false)}
                planId={planId}
                planName={currentPlanDetails?.plan_name || planName || planId}
            />
            <DeleteEventModal
                open={!!pendingDeleteEvent}
                onClose={() => setPendingDeleteEvent(null)}
                eventId={pendingDeleteEvent?.eventId ?? ''}
                eventName={pendingDeleteEvent?.eventName ?? ''}
            />
            <KeepFlagsModal pending={pendingKeepFlag} onClose={() => setPendingKeepFlag(null)} />
            <KeepEventModal record={pendingKeepEvent} onClose={() => setPendingKeepEvent(null)} />
            <ImportEventFromPlanModal open={showImport} onClose={() => setShowImport(false)} targetPlanId={planId} />

            <OptimizePlanModal
                open={showOptimizePlanModal}
                onCancel={() => setShowOptimizePlanModal(false)}
                onSubmitFinish={handleOptimizationSubmitFinish}
                planId={planId}
                config={tpoGenConfig.optimizePlanConfig}
            />

            <InfeasiblePlanDrawer
                open={showInfeasibleDrawer}
                onClose={() => setShowInfeasibleDrawer(false)}
                relaxedConstraints={infeasibilityData ?? []}
                config={infeasiblePlanConfig}
            />

            <OptimiseResultModal
                open={showOptimiseResultModal}
                onClose={() => setShowOptimiseResultModal(false)}
                jobId={optimizationJobId}
                planId={planId}
            />
            <ReportExportModal
                open={showExportModal}
                onClose={handleCloseExportModal}
                planId={activePlanId}
                reportKind="calendar"
                buildPayload={buildCalendarPayload}
            />

            <ViewSelectModal<CalendarIntervalType>
                open={showIntervalModal}
                title="Calendar View"
                label="Choose Calendar view"
                options={CALENDAR_INTERVAL_OPTIONS}
                initialValue={calendarInterval}
                onCancel={() => setShowIntervalModal(false)}
                onApply={handleApplyInterval}
                testId={TPO_CALENDAR_INTERVAL_MODAL}
            />
        </div>
    );
};

export default CalendarView;
